<script>
/**
 * @component DoctorBulkImport
 * @description Modal de subida masiva de prestadores desde Excel.
 *              Flow en 3 estados (controlado por `state` data):
 *              - 'idle'      → ofrece descargar plantilla + selector de archivo
 *              - 'uploading' → spinner mientras el API procesa
 *              - 'done'      → resumen (imported / duplicates / errors)
 *
 *              Permission gating en el padre (`ProviderDoctorList.vue`): solo
 *              admin debería abrirlo. El API tambien valida `isAdmin()` y
 *              devuelve 403 si no.
 *
 *              Errors por fila van a una tabla descargable como JSON. No
 *              bloquea — el padre recibe `@imported` con counts para
 *              refrescar el listado.
 * @example
 *   <DoctorBulkImport
 *     v-if="showImport"
 *     @close="showImport = false"
 *     @imported="onImported"
 *   />
 */
import doctorService from "@/services/doctor";
import Spinner from "@/components/ui/Spinner";

const TEMPLATE_PATH = "/plantilla-prestadores.xlsx";

export default {
  name: "DoctorBulkImport",

  components: { Spinner },

  data() {
    return {
      state: "idle", // 'idle' | 'uploading' | 'done'
      file: null,
      fileList: [],
      result: null, // { total, imported, duplicates, errors[] }
      errorMsg: null,
    };
  },

  computed: {
    /**
     * @description URL absoluta de la plantilla, sirviendo desde el bucket
     *              público del Dashboard (CloudFront). Vue CLI inyecta BASE_URL.
     */
    templateUrl() {
      return `${process.env.BASE_URL || "/"}${TEMPLATE_PATH.replace(
        /^\//,
        ""
      )}`;
    },
  },

  methods: {
    /**
     * @description Handler del `<el-upload :on-change>`. Guarda el File en data
     *              sin auto-subir (esperamos que el user haga click "Importar").
     * @param {Object} fileWrapper - Wrapper Element UI { raw: File, ... }
     */
    onFileChange(fileWrapper) {
      this.file = fileWrapper.raw;
      this.fileList = [fileWrapper];
      this.errorMsg = null;
    },

    /**
     * @description Quita el file del selector.
     */
    onFileRemove() {
      this.file = null;
      this.fileList = [];
    },

    /**
     * @description Submit del archivo al endpoint. Cambia state y muestra
     *              resumen al terminar. Emite `@imported` con counts para que
     *              el padre refresque listado.
     */
    async onSubmit() {
      if (!this.file) {
        this.errorMsg = "Seleccioná un archivo .xlsx primero";
        return;
      }
      this.state = "uploading";
      this.errorMsg = null;
      try {
        this.result = await doctorService.bulkImport(this.file);
        this.state = "done";
        this.$emit("imported", this.result);
      } catch (err) {
        this.state = "idle";
        this.errorMsg =
          err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : err.message || "Error inesperado al importar";
      }
    },

    /**
     * @description Genera un JSON descargable con los errores por fila para
     *              que el admin los revise en su Excel y reintente.
     */
    downloadErrorReport() {
      if (
        !this.result ||
        !this.result.errors ||
        this.result.errors.length === 0
      )
        return;
      const blob = new Blob([JSON.stringify(this.result.errors, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "errores-importacion-prestadores.json";
      a.click();
      URL.revokeObjectURL(url);
    },

    /**
     * @description Cierra el modal. Si hubo import exitoso (state==='done'),
     *              el padre ya hizo refresh por el @imported.
     */
    onClose() {
      this.$emit("close");
    },
  },
};
</script>

<template lang="pug">
el-dialog.doctor-bulk-import(
  :visible="true"
  title="Importar prestadores desde Excel"
  width="520px"
  :before-close="onClose"
  :close-on-click-modal="false"
)
  //- ── State: idle ─────────────────────────────────────────────────
  template(v-if="state === 'idle'")
    .dbi-step
      span.dbi-step__num 1
      .dbi-step__body
        p.dbi-step__title Descargá la plantilla
        a.button.button--white.dbi-template-link(:href="templateUrl" download)
          micon(name="description").button__icon
          span.button__text plantilla-prestadores.xlsx

    .dbi-step
      span.dbi-step__num 2
      .dbi-step__body
        p.dbi-step__title Completá los campos requeridos
        ul.dbi-fields-list
          li DNI · Nombre · Apellido · Email · Matrícula
          li Especialidad (separar con ";" si son varias)
          li Institución (separar con ";" si son varias)
          li Tipo Matrícula (mn/mp, default mn) · Provincia (default Nacional)
        p.dbi-help Duplicados (mismo DNI, email o matrícula) se saltean. El prestador recibe email de invitación para definir su contraseña.

    .dbi-step
      span.dbi-step__num 3
      .dbi-step__body
        p.dbi-step__title Subí el archivo
        el-upload.dbi-uploader(
          action=""
          :auto-upload="false"
          :file-list="fileList"
          :on-change="onFileChange"
          :on-remove="onFileRemove"
          :limit="1"
          accept=".xlsx"
        )
          el-button(icon="el-icon-paperclip" size="small") Seleccionar .xlsx

    .dbi-error(v-if="errorMsg") {{ errorMsg }}

    .dbi-footer
      el-button(@click="onClose") Cancelar
      el-button(type="primary" :disabled="!file" @click="onSubmit") Importar

  //- ── State: uploading ──────────────────────────────────────────────
  template(v-if="state === 'uploading'")
    .dbi-loading
      Spinner
      p Procesando archivo. No cierres esta ventana.

  //- ── State: done ───────────────────────────────────────────────────
  template(v-if="state === 'done' && result")
    .dbi-result
      h4.dbi-result__title ✓ Importación completada
      .dbi-result__line {{ result.total }} filas procesadas
      ul.dbi-result__counts
        li.dbi-result__counts-ok ✓ Insertados: {{ result.imported }}
        li.dbi-result__counts-dup ⊘ Duplicados: {{ result.duplicates }}
        li.dbi-result__counts-err(v-if="result.errors && result.errors.length")
          | ✗ Con errores: {{ result.errors.length }}
      .dbi-result__details(v-if="result.errors && result.errors.length")
        p Primeras 5 filas con error:
        ul.dbi-result__errlist
          li(v-for="(e, i) in result.errors.slice(0, 5)" :key="i")
            strong Fila {{ e.row }}:
            |  {{ e.error }}
        a.dbi-download-errors(href="#" @click.prevent="downloadErrorReport") ⤓ Descargar reporte completo
    .dbi-footer
      el-button(type="primary" @click="onClose") Cerrar
</template>

<style lang="scss" scoped>
.doctor-bulk-import {
  .dbi-step {
    display: flex;
    margin-bottom: 18px;

    &__num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: $primary;
      color: #fff;
      font-weight: 600;
      margin-right: 12px;
      flex-shrink: 0;
    }

    &__body {
      flex: 1;
    }

    &__title {
      font-weight: 600;
      margin: 4px 0 8px;
    }
  }

  .dbi-fields-list {
    margin: 4px 0 8px 0;
    padding-left: 18px;
    font-size: 13px;
    color: #555;

    li {
      margin-bottom: 2px;
    }
  }

  .dbi-help {
    font-size: 12px;
    color: #888;
    margin-top: 6px;
  }

  .dbi-template-link {
    display: inline-flex;
    align-items: center;
  }

  .dbi-error {
    background: #fff3f3;
    border: 1px solid #f5b1b1;
    color: #c0392b;
    padding: 8px 12px;
    border-radius: 4px;
    margin-top: 12px;
    font-size: 13px;
  }

  .dbi-footer {
    margin-top: 18px;
    text-align: right;
  }

  .dbi-loading {
    text-align: center;
    padding: 24px 0;

    p {
      margin-top: 12px;
      color: #555;
    }
  }

  .dbi-result {
    &__title {
      color: $appointment;
      margin: 0 0 12px;
    }

    &__line {
      font-size: 14px;
      color: #555;
      margin-bottom: 10px;
    }

    &__counts {
      list-style: none;
      padding: 0;
      margin: 12px 0;

      li {
        padding: 4px 0;
        font-weight: 500;
      }

      &-ok {
        color: $appointment;
      }
      &-dup {
        color: $recommendation;
      }
      &-err {
        color: $vital;
      }
    }

    &__details {
      background: #fafafa;
      padding: 10px 12px;
      border-radius: 4px;
      margin-top: 12px;
      font-size: 13px;
    }

    &__errlist {
      margin: 6px 0;
      padding-left: 18px;
      max-height: 120px;
      overflow-y: auto;

      li {
        margin-bottom: 4px;
      }
    }
  }

  .dbi-download-errors {
    display: inline-block;
    margin-top: 8px;
    font-size: 13px;
    color: $primary;
  }
}
</style>
